import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

const NEWSLETTER_FORM_URL = 'https://readdy.ai/api/form/d8uk8upl2p03kijan3t0';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setSubmitStatus('success');
      setEmail('');
      return;
    }
    if (!email.trim()) return;

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      await fetch(NEWSLETTER_FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      setSubmitStatus('success');
      setEmail('');
    } catch {
      setSubmitStatus('error');
    }
    setTimeout(() => setSubmitStatus('idle'), 3000);
  };

  return (
    <footer className="bg-background-900">
      <div className="max-w-[1400px] mx-auto px-6 py-14 md:py-20">
        {/* Top Section */}
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          {/* Left - Brand + Newsletter */}
          <div className="lg:w-2/5">
            <h4 className="text-2xl md:text-3xl text-background-50 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              AllChinaBuySpreadsheet
            </h4>
            <p className="text-background-300 text-sm leading-relaxed mb-6 max-w-sm">
              Curated China product intelligence for global dropshippers. Winning products, reliable suppliers, and profit analysis — all in one spreadsheet.
            </p>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="flex gap-0 max-w-sm"
            >
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email for updates"
                required
                className="flex-1 px-4 py-3 text-sm bg-transparent border border-background-600 rounded-l-full text-background-100 placeholder:text-background-500 outline-none focus:border-background-300 transition-colors"
              />
              <button
                type="submit"
                className="px-5 py-3 bg-background-100 text-foreground-800 rounded-r-full text-sm font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center"
              >
                <i className="ri-mail-send-line text-base"></i>
              </button>
            </form>

            {submitStatus === 'success' && (
              <p className="text-accent-400 text-xs mt-2">Subscribed! Check your inbox.</p>
            )}
            {submitStatus === 'error' && (
              <p className="text-red-400 text-xs mt-2">Something went wrong. Try again.</p>
            )}

            <p className="text-xl md:text-2xl text-background-100 mt-8 leading-snug" style={{ fontFamily: 'var(--font-heading)' }}>
              Winning Products<br />Start Here.
            </p>
          </div>

          {/* Right - Navigation Columns */}
          <div className="lg:w-3/5 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {/* Categories */}
            <div>
              <h5 className="text-background-200 text-sm font-semibold mb-4">Categories</h5>
              <ul className="space-y-2.5">
                <li><a href="https://kaspreadsheet.com/search-923/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">Perfumes</a></li>
                <li><a href="https://kaspreadsheet.com/search-924/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">Other Stuff</a></li>
                <li><a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">View All</a></li>
              </ul>
            </div>

            {/* Top Picks */}
            <div>
              <h5 className="text-background-200 text-sm font-semibold mb-4">Top Picks</h5>
              <ul className="space-y-2.5">
                <li><a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">Hoodies</a></li>
                <li><a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">T-Shirts</a></li>
                <li><a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">Jackets</a></li>
              </ul>
            </div>

            {/* Resources - Internal SEO Links */}
            <div>
              <h5 className="text-background-200 text-sm font-semibold mb-4">Resources</h5>
              <ul className="space-y-2.5">
                <li><Link to="/allchinabuyspreadsheet-guide" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">Complete Guide</Link></li>
                <li><Link to="/allchinabuyspreadsheet-guide" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">How It Works</Link></li>
                <li><Link to="/allchinabuyspreadsheet-guide" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">SEO Tips 2026</Link></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h5 className="text-background-200 text-sm font-semibold mb-4">Legal</h5>
              <ul className="space-y-2.5">
                <li><a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-400 text-sm hover:text-background-100 transition-colors cursor-pointer whitespace-nowrap">Main Site</a></li>
                <li><span className="text-background-400 text-sm cursor-default whitespace-nowrap">Privacy Policy</span></li>
                <li><span className="text-background-400 text-sm cursor-default whitespace-nowrap">Affiliate Disclosure</span></li>
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-14 pt-8 border-t border-background-700/50 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-background-500 text-xs">
            &copy; {new Date().getFullYear()} AllChinaBuySpreadsheet. Educational &amp; research purposes only.
          </p>
          <div className="flex items-center gap-3">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-8 h-8 flex items-center justify-center rounded border border-background-600 text-background-400 hover:text-background-100 hover:border-background-400 transition-colors cursor-pointer" aria-label="Main Site">
              <i className="ri-global-line text-sm"></i>
            </a>
            <span className="w-8 h-8 flex items-center justify-center rounded border border-background-600 text-background-400 cursor-default" aria-label="Twitter">
              <i className="ri-twitter-x-line text-sm"></i>
            </span>
            <span className="w-8 h-8 flex items-center justify-center rounded border border-background-600 text-background-400 cursor-default" aria-label="Instagram">
              <i className="ri-instagram-line text-sm"></i>
            </span>
            <span className="w-8 h-8 flex items-center justify-center rounded border border-background-600 text-background-400 cursor-default" aria-label="LinkedIn">
              <i className="ri-linkedin-line text-sm"></i>
            </span>
          </div>
          <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-background-500 text-xs hover:text-background-300 transition-colors cursor-pointer whitespace-nowrap">
            Visit Main Store →
          </a>
        </div>
      </div>
    </footer>
  );
}